// Прозорецът „История на операциите" по една сметка.
//
// Отваря се с клик на ред в пет справки (Справка сметки, Сторно, Картови
// плащания, Отстъпки, Променени цени) и от Дашборда. Лентата с действия долу
// се рендира САМО когато справката подаде `showCancelButton` — останалите са
// за четене и не бива да пускат фискален сторно документ.
//
// Тук стои само сглобяването; частите живеят в `billOperations/`.

import { Modal } from "antd";
import { useTranslation } from "react-i18next";

import { modalStyles, modalWrapStyle } from "../../../utils/modalStyles";
import { useViewport } from "../../../hooks/useViewport";
import { useBillOperations } from "../../../hooks/useBillOperations";
import type { BillHistory } from "../../../api/reportsBillsHistory";
import { InvoiceCreateModal } from "../../ordering/components/tables/invoicing/InvoiceCreateModal";
import { InvoicePreviewModal } from "../../ordering/components/tables/invoicing/InvoicePreviewModal";
import { InvoiceBillPickerModal } from "./InvoiceBillPickerModal";
import { BillOperationsFooter } from "./BillOperationsFooter";
import { BillOperationsHeader } from "./billOperations/BillOperationsHeader";
import { BillOperationsTotals } from "./billOperations/BillOperationsTotals";
import { BillOperationsTimeline } from "./billOperations/BillOperationsTimeline";
import { BillOperationsCardPayments } from "./billOperations/BillOperationsCardPayments";
import { useBillOperationsActions } from "./billOperations/useBillOperationsActions";

interface BillOperationsModalProps {
  bill: BillHistory | null;
  open: boolean;
  onClose: () => void;
  onBillUpdated?: () => void;
  showCancelButton?: boolean;
}

export const BillOperationsModal = ({
  bill,
  open,
  onClose,
  onBillUpdated,
  showCancelButton = false,
}: BillOperationsModalProps) => {
  const { i18n } = useTranslation();
  const { isMobile, isNarrow } = useViewport();

  const { data, isLoading } = useBillOperations(bill?.id ?? null);
  const actions = useBillOperationsActions({
    bill,
    summary: data?.summary,
    onClose,
    onBillUpdated,
  });

  if (!bill) return null;

  const cardPayments = bill.card_payments ?? [];
  const { invoiceFlow } = actions;

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        centered
        closable={false}
        destroyOnHidden
        width={isMobile ? undefined : isNarrow ? 560 : 720}
        styles={modalStyles}
        style={modalWrapStyle}
        title={null}
      >
        <div className="flex max-h-[92vh] flex-col bg-surface">
          <BillOperationsHeader
            data={data}
            bill={bill}
            isNarrow={isNarrow}
            onClose={onClose}
          />

          {data ? (
            <BillOperationsTotals summary={data.summary} isNarrow={isNarrow} />
          ) : null}

          <BillOperationsTimeline
            events={data?.events}
            isLoading={isLoading}
            billName={bill.name}
            billWaiterId={data?.bill.waiter?.id ?? null}
            language={i18n.language}
            isNarrow={isNarrow}
          />

          {cardPayments.length > 0 ? (
            <BillOperationsCardPayments
              payments={cardPayments}
              isNarrow={isNarrow}
            />
          ) : null}

          {showCancelButton ? (
            <BillOperationsFooter
              isMobile={isMobile}
              isNarrow={isNarrow}
              canCancel={actions.canCancel}
              canRestore={actions.canRestore}
              canInvoice={actions.canInvoice}
              isInvoiced={!!bill.is_invoiced}
              canPrintReceiptCopy={actions.canPrintReceiptCopy}
              receiptCopyHint={actions.receiptCopyHint}
              cancelLoading={actions.cancelLoading}
              restoreLoading={actions.restoreLoading}
              receiptCopyLoading={actions.receiptCopyLoading}
              onCancelAll={actions.handleCancelAll}
              onRestoreAll={actions.handleRestoreAll}
              onInvoice={actions.handleInvoice}
              onPrintReceiptCopy={actions.handlePrintReceiptCopy}
            />
          ) : null}
        </div>
      </Modal>

      <InvoiceBillPickerModal
        open={invoiceFlow.billSelectOpen}
        currentBill={bill}
        onClose={invoiceFlow.closeBillSelect}
        onConfirm={invoiceFlow.confirmBillSelect}
      />
      <InvoiceCreateModal
        open={invoiceFlow.invoiceCreateOpen}
        onClose={invoiceFlow.closeInvoiceCreate}
        bills={
          invoiceFlow.selectedBills.length > 0
            ? invoiceFlow.selectedBills
            : [bill]
        }
        onSaved={invoiceFlow.onInvoiceSaved}
      />
      <InvoicePreviewModal
        open={invoiceFlow.previewInvoice != null}
        invoice={invoiceFlow.previewInvoice}
        onClose={invoiceFlow.closePreview}
      />
    </>
  );
};

export default BillOperationsModal;
